import * as yup from 'yup';
import { Schema } from 'yup';
import { DynamicFormFieldTypes } from '@common/types';
import {
  addDisableConditionToSchema,
  addOptionalToSchema,
  addRequiredToSchema,
  checkboxSchema,
  citySchema,
  countrySchema,
  countySchema,
  pastDateSchema,
  disabledSchema,
  emailSchema,
  dateSchema,
  googlePlacesSchema,
  mbiSchema,
  phoneSchema,
  radioButtonSchema,
  repeaterTextareaSchema,
  repeaterTextFieldSchema,
  searchSchema,
  selectSchema,
  simpleStringSchema,
  stateSchema,
  streetSchema,
  suiteSchema,
  textAreaSchema,
  textSchema,
  zipSchema,
  repeaterSchema,
  requiredErrorMessage,
} from '@common/schema';

const getMatchingSchema = (type: DynamicFormFieldTypes): Schema => {
  switch (type) {
    case 'building_search':
      return searchSchema;
    case 'provider_search':
      return searchSchema;
    case 'respite_partner_search':
      return searchSchema;
    case 'text':
      return textSchema;
    case 'street':
      return streetSchema;
    case 'suite':
      return suiteSchema;
    case 'city':
      return citySchema;
    case 'state':
      return stateSchema;
    case 'zip':
      return zipSchema;
    case 'county':
      return countySchema;
    case 'country':
      return countrySchema;
    case 'phone':
      return phoneSchema;
    case 'email':
      return emailSchema;
    case 'date':
      return dateSchema;
    case 'time':
      return pastDateSchema;
    case 'mbi':
      return mbiSchema;
    case 'checkbox':
    case 'checkbox_list':
      return checkboxSchema;
    case 'select':
    case 'multi_select':
      return selectSchema;
    case 'repeater_textarea':
      return repeaterTextareaSchema;
    case 'repeater_textfield':
      return repeaterTextFieldSchema;
    case 'repeater_icd10':
      return repeaterSchema(textSchema.required(requiredErrorMessage));
    case 'radio':
    case 'radio_vertical':
      return radioButtonSchema;
    case 'textbox':
      return textAreaSchema;
    case 'google_places_id':
      return googlePlacesSchema;
    case 'integer':
      return textSchema;
    case 'float':
    default:
      return simpleStringSchema;
  }
};

export const mapDynamicFormSchema = ({
  type,
  optional = false,
  disable_if,
  disabled,
}: {
  name: string;
  type: DynamicFormFieldTypes;
  optional: boolean;
  disable_if?: string;
  disabled?: boolean;
}): Schema => {
  let schema = getMatchingSchema(type);

  if (!optional) {
    schema = addRequiredToSchema(schema, type);
  } else {
    schema = addOptionalToSchema(schema);
  }

  if (!disabled && disable_if) {
    schema = addDisableConditionToSchema({ schema, disable_if });
  } else if (disabled) {
    schema = disabledSchema;
  }

  return schema;
};

interface IFormEntry {
  name: string;
  type: DynamicFormFieldTypes;
  optional?: boolean;
  disable_if?: string;
  editable?: boolean;
}

export const schemaAggregator = (formEntries: IFormEntry[]) => {
  const schemaObject = Object.create({});

  formEntries.forEach(entry => {
    const schema = mapDynamicFormSchema({
      name: entry.name,
      type: entry.type,
      optional: !!entry.optional,
      disable_if: entry.disable_if,
      disabled: entry.editable === false,
    });
    Object.assign(schemaObject, { [entry.name]: schema });
  });
  const assembledSchema = yup.object().shape(schemaObject);

  return assembledSchema;
};
